import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { brl, durationMin } from "../lib/format";
import { DatePickerUS } from "../components/ui/date-picker-us";
import { useI18n } from "../i18n/I18nContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  Line,
  LineChart,
} from "recharts";

export default function Reports() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    const { data } = await api.get(`/reports/summary?from=${from}&to=${to}`);
    setData(data);
  }, [from, to]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Análises")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">{t("Relatórios")}</h1>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-sm">
            <div className="text-slate-500 mb-1">{t("De")}</div>
            <DatePickerUS
              testId="reports-from"
              value={from}
              onChange={setFrom}
            />
          </label>
          <label className="text-sm">
            <div className="text-slate-500 mb-1">{t("Até")}</div>
            <DatePickerUS
              testId="reports-to"
              value={to}
              onChange={setTo}
            />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi label={t("Limpezas no período")} value={data?.total_jobs ?? "—"} />
        <Kpi label={t("Finalizadas")} value={data?.finished ?? "—"} />
        <Kpi
          label={t("Tempo médio")}
          value={durationMin(data?.avg_duration_min || 0)}
        />
        <Kpi
          label={t("Faturamento")}
          value={brl(data?.revenue || 0)}
          accent="text-blue-700"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="cm-card p-5">
          <h3 className="font-semibold mb-3">{t("Limpezas por dia")}</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={data?.by_day || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#2563EB"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cm-card p-5">
          <h3 className="font-semibold mb-3">{t("Produtividade por equipe")}</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={data?.by_team || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="team" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="cm-card p-4 sm:p-5 mt-6">
        <h3 className="font-semibold mb-3">{t("Resumo financeiro")}</h3>
        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-widest leading-tight">
              {t("Receita")}
            </div>
            <div className="text-base sm:text-2xl font-bold text-emerald-600 truncate mt-1">
              {brl(data?.revenue || 0)}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-widest leading-tight">
              <span className="block sm:inline">{t("Custos")}</span>
              <span className="block sm:inline text-[9px] sm:text-xs"> {t("(colab.)")}</span>
            </div>
            <div className="text-base sm:text-2xl font-bold text-red-600 truncate mt-1">
              {brl(data?.cost || 0)}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-widest leading-tight">
              {t("Lucro")}
            </div>
            <div className="text-base sm:text-2xl font-bold text-slate-900 truncate mt-1">
              {brl(data?.profit || 0)}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Kpi({ label, value, accent = "" }) {
  return (
    <div className="cm-card p-3 sm:p-5 min-w-0">
      <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 font-semibold leading-tight">
        {label}
      </div>
      <div
        className={`mt-1 sm:mt-2 text-lg sm:text-3xl font-bold font-[Outfit] truncate ${accent || "text-slate-900"}`}
      >
        {value}
      </div>
    </div>
  );
}
